import React, { useState, FormEvent } from "react";
import ButtonLoader from "../../../../UI/Loaders/Loaders";

import {
  handleSwalSuccessAlert,
  handleSwalErrorAlert,
} from "../../../../helpers";
import { errorHandler } from "../../../../helpers/errorHandler";
import { postData } from "../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";

interface IDownloadStatementModalProps {
  dataRecord: {
    accountId: string;
    accountNumber: string;
    accountName: string;
    customerEmail?: string;
    walletProviderId?: number;
  };
  onCloseModal: () => void;
}

const DownloadAccountStatementModal = ({
  dataRecord,
  onCloseModal,
}: IDownloadStatementModalProps) => {
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    format: "pdf",
    sendToThirdParty: false,
    destinationEmail: dataRecord.customerEmail || "",
    transactionPin: "",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(null);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      sendToThirdParty: checked,
      destinationEmail: checked ? "" : dataRecord.customerEmail || "",
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.startDate) {
      setError("Please select a start date");
      return false;
    }
    if (!formData.endDate) {
      setError("Please select an end date");
      return false;
    }
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setError("Start date cannot be after end date");
      return false;
    }
    if (!formData.destinationEmail) {
      setError("Please provide a destination email");
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.destinationEmail)) {
      setError("Please provide a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsDownloading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const fileTypeMap: { [key: string]: string } = {
        pdf: "PDF",
        excel: "Excel",
        pdfandexcel: "PDFAndExcel",
      };

      const requestBody = {
        email: formData.destinationEmail,
        accountNumber: String(dataRecord.accountNumber),
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        fileType: fileTypeMap[formData.format] || "PDF",
        walletProviderId: dataRecord.walletProviderId || 0,
      };

      const response = await postData(
        ajaxEndpoints.DB_REQUEST_BANK_STATEMENT,
        requestBody
      );

      const message =
        response?.data?.message || "Statement request submitted successfully";
      handleSwalSuccessAlert(message, () => {
        onCloseModal();
      });
    } catch (err: any) {
      const errorMessage =
        errorHandler(err) ||
        err?.message ||
        "Failed to generate statement. Please try again.";
      handleSwalErrorAlert(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div
      className="Modal__dialog md-modal animated slideInDown"
      role="document"
    >
      <div className="Modal__content" style={{ overflow: "hidden" }}>
        <div className="Modal__body">
          <div style={{ minHeight: "400px" }}>
            <h3
              className="Modal__title p-b-2 text-center"
              style={{ fontWeight: "bold" }}
            >
              Generate Bank Statement
            </h3>

            <div className="p-l-2 p-r-2">
              <div className="mb-3">
                <p>
                  <strong>Account Number:</strong>{" "}
                  <strong>{dataRecord.accountNumber}</strong>
                </p>
                <p>
                  <strong>Account Name:</strong>{" "}
                  <strong>{dataRecord.accountName}</strong>
                </p>
              </div>

              {error && (
                <div
                  className="alert alert-danger"
                  role="alert"
                  style={{ marginBottom: "1rem" }}
                >
                  {error}
                </div>
              )}

              {successMessage && (
                <div
                  className="alert alert-success"
                  role="alert"
                  style={{ marginBottom: "1rem" }}
                >
                  {successMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="startDate">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    className="form-control"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    max={today}
                    disabled={isDownloading}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    className="form-control"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    max={today}
                    disabled={isDownloading}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="format">
                    Select Statement Format{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    id="format"
                    name="format"
                    className="form-control"
                    value={formData.format}
                    onChange={handleInputChange}
                    disabled={isDownloading}
                  >
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                    <option value="pdfandexcel">PDF and Excel</option>
                  </select>
                </div>

                <div className="form-group">
                  <div className="">
                    <label htmlFor="sendToThirdParty" className="mt-2">
                      Send to third party email address
                    </label>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="sendToThirdParty"
                        name="sendToThirdParty"
                        checked={formData.sendToThirdParty}
                        onChange={handleSwitchChange}
                        disabled={isDownloading}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="sendToThirdParty"
                      ></label>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="destinationEmail">
                    Destination Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="destinationEmail"
                    name="destinationEmail"
                    className="form-control"
                    value={formData.destinationEmail}
                    onChange={handleInputChange}
                    disabled={isDownloading || !formData.sendToThirdParty}
                    placeholder={
                      formData.sendToThirdParty
                        ? "Enter destination email"
                        : "Customer email"
                    }
                    required
                  />
                  <small className="form-text text-muted">
                    {formData.sendToThirdParty
                      ? "Statement will be sent to this email address"
                      : "Statement will be sent to customer's email"}
                  </small>
                </div>

                <div className="Form__action-buttons m-t-2">
                  <button
                    type="button"
                    className="btn zoom-element btn login-btn m-0 mr-auto"
                    onClick={onCloseModal}
                    disabled={isDownloading}
                  >
                    <i className="fas fa-times-circle"></i>
                    &nbsp; Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn zoom-element btn login-btn m-0 ml-auto"
                    disabled={isDownloading}
                  >
                    <i className="fas fa-download"></i>
                    &nbsp; Generate Statement{" "}
                    {isDownloading && <ButtonLoader />}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadAccountStatementModal;
